import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
    LayoutDashboard, Calendar, Users, Scissors, UserCog,
    Receipt, Crown, BarChart3, Settings as Cog, ExternalLink, CreditCard,
} from "lucide-react";
import { bookingUrl as buildBookingUrl } from "@/lib/site";
import { filteredNav } from "@/lib/permissions";
import BrandLogo from "@/components/BrandLogo";
import AppTopBar from "@/components/AppTopBar";
import QuickActionsFab from "@/components/QuickActionsFab";
import sidebarBg from "@/assets/sidebar-bg.png";

const linkMeta = {
    dashboard: { label: "Dashboard", icon: LayoutDashboard },
    calendar: { label: "Calendar", icon: Calendar },
    customers: { label: "Customers", icon: Users },
    services: { label: "Services", icon: Scissors },
    staff: { label: "Staff", icon: UserCog },
    pos: { label: "Point of Sale", icon: Receipt },
    memberships: { label: "Memberships", icon: Crown },
    reports: { label: "Reports", icon: BarChart3 },
    billing: { label: "Subscription", icon: CreditCard },
    settings: { label: "Settings", icon: Cog },
};

const links = Object.entries(linkMeta).map(([to, meta]) => ({ to, ...meta }));

/* Sidebar type */
const label = "text-[15px] font-semibold text-white tracking-wide";
const labelSm = "text-xs font-medium text-white/90 tracking-wide";
const accent = "text-xs font-semibold text-[#FFE9A8] tracking-widest uppercase";
const navActive =
    "font-semibold text-[#FFF4D6] bg-[rgba(201,162,39,0.28)] border-l-[3px] border-[#D4AF37] shadow-[inset_0_0_20px_rgba(212,175,55,0.08)]";

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const visibleLinks = filteredNav(user?.role).map(({ to }) => links.find((l) => l.to === to)).filter(Boolean);

    return (
        <div className="flex h-screen overflow-hidden bg-background">
            <aside
                className="relative h-full w-64 shrink-0 flex flex-col overflow-hidden border-r border-[#E8C547]/20 bg-cover bg-top bg-no-repeat"
                style={{ backgroundImage: `url(${sidebarBg})` }}
                data-testid="app-sidebar"
            >
                <div
                    className="absolute inset-0 z-[1] pointer-events-none"
                    style={{
                        background: [
                            "linear-gradient(to right, rgba(48, 32, 22, 0.46) 0%, rgba(82, 62, 44, 0.24) 58%, rgba(130, 102, 72, 0.08) 100%)",
                            "linear-gradient(to bottom, rgba(42, 28, 18, 0.18) 0%, transparent 38%, transparent 72%, rgba(42, 28, 18, 0.16) 100%)",
                        ].join(", "),
                    }}
                    aria-hidden
                />

                <div className="relative z-10 flex flex-col flex-1 min-h-0 font-['Outfit',system-ui,sans-serif] antialiased">
                    <div className="px-6 py-5 border-b border-[#E8C547]/25">
                        <BrandLogo
                            size={32}
                            textClass="font-serif text-2xl font-semibold text-white"
                            accentClass={`${accent}`}
                        />
                        {user?.tenant_name && (
                            <p className={`mt-1 truncate ${labelSm}`} data-testid="sidebar-salon-name">
                                {user.tenant_name}
                            </p>
                        )}
                    </div>

                    <div className="scrollbar-sidebar flex min-h-0 flex-1 flex-col overflow-y-auto">
                        <nav className="flex-1 space-y-1 px-3 py-4">
                            {visibleLinks.map(({ to, label: text, icon: Icon }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    data-testid={`nav-${to}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                                            isActive
                                                ? navActive
                                                : `${label} hover:text-[#FFE9A8] hover:bg-white/5`
                                        }`
                                    }
                                >
                                    <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                                    {text}
                                </NavLink>
                            ))}
                        </nav>

                        {user?.tenant_slug && (
                            <div className="mt-auto border-t border-[#E8C547]/25 px-3 py-3">
                                <a
                                    href={buildBookingUrl(user.tenant_slug)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="public-booking-link"
                                    className={`flex items-center gap-2 px-3 py-2 ${labelSm} hover:text-[#FFE9A8]`}
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    Public booking page
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            <div className="scrollbar-salon flex h-full min-w-0 flex-1 flex-col overflow-y-auto bg-background">
                <AppTopBar onLogout={handleLogout} />
                <main className="flex-1 bg-background">
                    <Outlet />
                </main>
                <QuickActionsFab />
            </div>
        </div>
    );
}
